import { useEffect, useRef, useState } from 'react';
import { AgentToggle } from '../agent/AgentControls';
import { shortAddress } from '../wallet/address';
import { useGhost, useWallet } from '../wallet/context';
import { GhostDialog, WalletPickerModal } from './WalletModals';
import { useT } from '../i18n/useT';

const ITEM = 'block w-full rounded-md px-3 py-2 text-start text-sm hover:bg-surface-2';

/**
 * Header wallet control: Connect → wallet picker; connected chip with menu (copy / ghost mode / 1-click trading #688 /
 * disconnect);
 * GHOST badge + exit while watching an address.
 */
export default function WalletButton() {
  const wallet = useWallet();
  const t = useT();
  const { ghostAddress, ghostError, exitGhost, clearGhostError } = useGhost();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [ghostOpen, setGhostOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const viewed = ghostAddress ?? wallet.address;
  const busy = wallet.status === 'connecting' || wallet.status === 'reconnecting';

  const copy = async () => {
    if (!viewed) return;
    try {
      await navigator.clipboard.writeText(viewed);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked (insecure context / permissions) */
    }
  };

  const run = (fn: () => void) => () => {
    setMenuOpen(false);
    fn();
  };

  return (
    <>
      {!viewed ? (
        <button
          type="button"
          data-tour="wallet"
          data-testid="connect-wallet"
          onClick={() => setPickerOpen(true)}
          disabled={busy}
          className="whitespace-nowrap rounded-lg bg-accent px-3 py-1.5 text-xs font-semibold text-bg disabled:opacity-60 sm:text-sm"
        >
          {busy ? t('wallet.connecting') : t('wallet.connect')}
        </button>
      ) : (
        <div ref={menuRef} className="relative" data-tour="wallet">
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={ghostAddress ? t('wallet.ghost_aria', { address: ghostAddress }) : t('wallet.wallet_short', { address: viewed })}
            onClick={() => setMenuOpen((o) => !o)}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-2 py-1 text-xs font-medium sm:px-2.5 sm:text-sm ${
              ghostAddress ? 'border-warn/70 bg-warn/10' : 'border-border bg-surface-2'
            }`}
          >
            {ghostAddress ? (
              <span className="rounded bg-warn px-1 py-px text-[10px] font-bold tracking-wide text-warn-fg">{t('wallet.ghost_badge')}</span>
            ) : (
              <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-yes" />
            )}
            <span className="font-mono">{shortAddress(viewed)}</span>
            <span aria-hidden className="hidden text-muted sm:inline">
              ▾
            </span>
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="card absolute end-0 top-full z-30 mt-1 w-64 max-w-[calc(100vw-2rem)] p-1 shadow-xl"
            >
              <div className="px-3 py-2 text-xs text-muted">
                {ghostAddress
                  ? t('wallet.watching')
                  : wallet.walletInfo
                    ? t('wallet.connected_with', { wallet: wallet.walletInfo.name })
                    : t('wallet.connected')}
                <div className="mt-0.5 break-all font-mono text-fg">{viewed}</div>
              </div>
              <button type="button" role="menuitem" className={ITEM} onClick={() => void copy()}>
                {copied ? t('common.copied') : t('wallet.copy')}
              </button>
              <button type="button" role="menuitem" className={ITEM} onClick={run(() => setGhostOpen(true))}>
                {ghostAddress ? t('wallet.change_ghost') : t('wallet.ghost_mode_item')}
              </button>
              {ghostAddress && (
                <button type="button" role="menuitem" className={`${ITEM} text-warn`} onClick={run(exitGhost)}>
                  {t('wallet.exit_ghost')}
                </button>
              )}
              {wallet.status === 'connected' && wallet.address && (
                <>
                  <div className="my-1 border-t border-border" />
                  <AgentToggle variant="menu" onAction={() => setMenuOpen(false)} />
                </>
              )}
              <div className="my-1 border-t border-border" />
              {wallet.status === 'connected' && wallet.address ? (
                <>
                  {ghostAddress && (
                    <div className="px-3 py-1 text-xs text-muted">{t('wallet.wallet_short', { address: shortAddress(wallet.address) })}</div>
                  )}
                  <button type="button" role="menuitem" className={`${ITEM} text-danger`} onClick={run(wallet.disconnect)}>
                    {t('wallet.disconnect')}
                  </button>
                </>
              ) : (
                <button type="button" role="menuitem" className={ITEM} onClick={run(() => setPickerOpen(true))}>
                  {t('wallet.connect_wallet')}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <WalletPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onGhost={() => {
          setPickerOpen(false);
          setGhostOpen(true);
        }}
      />
      <GhostDialog open={ghostOpen} onClose={() => setGhostOpen(false)} />

      {ghostError && (
        <div
          role="status"
          className="card fixed bottom-4 start-4 end-4 z-40 flex items-start gap-3 p-3 text-sm sm:start-auto sm:w-96"
        >
          <span className="flex-1 text-danger">{ghostError}</span>
          <button type="button" onClick={clearGhostError} aria-label={t('common.dismiss')} className="text-muted hover:text-fg">
            ✕
          </button>
        </div>
      )}
    </>
  );
}
